import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Icon } from '../components/Icon';
import { LearnedNotes } from '../components/LearnedNotes';
import { Sources } from '../components/Sources';
import type { MemoryRecord } from '@jarvis/shared';
import { api } from '../lib/api';
import { toast } from '../components/toast';
import { CardHead, EmptyState, Notice, PageHead, Progress, Skeleton } from '../components/ui';
import { chat, useChat } from '../lib/ai';
import { fmt } from '../lib/format';
import { usePrefs } from '../lib/prefs';
import { useVoiceState, voice } from '../lib/voice';

/* Conversa com o Jarvis: histórico guardado no servidor, resposta em tempo real,
   voz do navegador e sugestões de memória que só entram se você aceitar. */

function UsagePanel() {
  const { status } = useChat();
  if (!status) return null;
  const pct = (n: number, of: number) => Math.min(100, (n / of) * 100);
  const dayPct = pct(status.today.tokens, status.limits.daily);
  const monthPct = pct(status.month.tokens, status.limits.monthly);
  return (
    <section className="card">
      <CardHead icon="gauge" title="Consumo e limites" />
      <div className="usage">
        <div className="usage-row"><div className="top"><span>Hoje</span><span className="mono">{fmt.num(status.today.tokens)} / {fmt.num(status.limits.daily)} tokens</span></div><Progress value={dayPct} /></div>
        <div className="usage-row"><div className="top"><span>Este mês</span><span className="mono">{fmt.num(status.month.tokens)} / {fmt.num(status.limits.monthly)} tokens</span></div><Progress value={monthPct} /></div>
        {status.search.enabled && (
          <div className="usage-row"><div className="top"><span>Buscas na web hoje</span><span className="mono">{status.today.searches} / {status.search.dailyLimit}</span></div><Progress value={pct(status.today.searches, status.search.dailyLimit)} /></div>
        )}
        <p className="row-sub">{status.today.requests} {status.today.requests === 1 ? 'mensagem respondida' : 'mensagens respondidas'} hoje. O servidor confere o limite antes de cada chamada e para quando ele acaba.</p>
        {Math.max(dayPct, monthPct) >= status.limits.alertPercent && (
          <Notice kind="warn" icon="alert">Você já usou {Math.round(Math.max(dayPct, monthPct))}% de um dos limites.</Notice>
        )}
      </div>
      <dl className="kv mt-10">
        <dt>Provedor</dt><dd>Anthropic (Claude)</dd>
        <dt>Modelo</dt><dd className="mono">{status.model}</dd>
      </dl>
    </section>
  );
}

function Suggestions() {
  const { suggestions } = useChat();
  if (!suggestions.length) return null;
  const accept = async (key: number) => {
    try { await chat.accept(key); toast('Guardado na memória.', 'check'); }
    catch (e) { toast(e instanceof Error ? e.message : 'Não foi possível guardar.', 'alert'); }
  };
  return (
    <div className="mem-suggest" aria-live="polite">
      {suggestions.map((s) => (
        <div key={s.key} className={`mem-card ${s.state}`}>
          <Icon name="brain" />
          <div className="row-main">
            <div className="row-sub">O Jarvis sugere lembrar <span className="mono">· {s.category}</span></div>
            <div className="row-title">{s.text}</div>
          </div>
          {s.state === 'saved'
            ? <span className="badge badge-signal"><Icon name="check" />Guardado</span>
            : <>
                <button type="button" className="btn btn-sm btn-primary" disabled={s.state === 'saving'} onClick={() => accept(s.key)}>{s.state === 'saving' ? 'Guardando…' : 'Guardar'}</button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => chat.dismiss(s.key)}>Agora não</button>
              </>}
        </div>
      ))}
    </div>
  );
}

export function Conversation() {
  const c = useChat();
  const v = useVoiceState();
  const { displayName } = usePrefs();
  const [text, setText] = useState('');
  const [speak, setSpeak] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const speakRef = useRef(speak);
  speakRef.current = speak;

  useEffect(() => {
    void chat.loadStatus();
    if (!chat.get().historyLoaded) void chat.loadHistory();
  }, []);

  const submitText = async (q: string) => {
    if (!q.trim()) return;
    setText('');
    /* Com "responder falando", a voz começa já na primeira frase da resposta. */
    const sp = speakRef.current ? voice.speaker() : null;
    await chat.send(q, displayName, { voice: Boolean(sp), onDelta: (t) => sp?.push(t) });
    sp?.end();
  };

  useEffect(() => {
    if (!v.final) return;
    const said = v.final;
    voice.clear();
    void submitText(said);
  }, [v.final]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [c.messages.length, c.draft, c.pendingQuestion, c.suggestions.length, c.learned.length]);

  /* Quantas coisas aprendidas sozinho esperam a sua revisão. */
  const [toReview, setToReview] = useState(0);
  useEffect(() => {
    api.get<{ items: MemoryRecord[] }>('/api/memories')
      .then((r) => setToReview(r.items.filter((m) => m.auto && !m.reviewedAt).length), () => {});
  }, [c.learned.length]);

  const onSubmit = (e: FormEvent) => { e.preventDefault(); void submitText(text); };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submitText(text); }
  };
  const clear = async () => {
    if (!window.confirm('Apagar todo o histórico da conversa? A memória não é afetada.')) return;
    try { await chat.clear(); toast('Histórico apagado.', 'check'); }
    catch (e) { toast(e instanceof Error ? e.message : 'Não foi possível apagar.', 'alert'); }
  };

  if (!c.status && !c.statusError) return <div className="page"><PageHead title="Conversa" /><Skeleton kind="block" /></div>;

  if (c.status && !c.status.configured) {
    return (
      <div className="page">
        <PageHead title="Conversa" lead="Converse com o Jarvis por texto ou voz." />
        <EmptyState icon="brain" title="Nenhum modelo de IA configurado"
          text="Para conversar de verdade, o servidor precisa de uma chave da Anthropic (ANTHROPIC_API_KEY) cadastrada na Render. A chave fica só no servidor, nunca no navegador. Até lá, a barra de comando da tela inicial responde apenas a comandos locais."
          actions={[{ label: 'Ver provedores', href: '#/configuracoes/provedores', icon: 'plug', primary: true }, { label: 'Voltar ao início', href: '#/visao-geral', icon: 'home' }]} />
      </div>
    );
  }

  const micTitle = !v.supported.listen ? 'Este navegador não reconhece fala.' : v.listening ? 'Parar de escutar' : 'Falar';
  const empty = c.historyLoaded && !c.messages.length && !c.streaming;

  return (
    <div className="page">
      <PageHead title="Conversa" lead="O Jarvis responde usando seus projetos, sua rotina, a memória e os cabeçalhos do Gmail, e pesquisa na web quando precisa de algo atual, sempre mostrando as fontes."
        actions={c.messages.length > 0 && <button type="button" className="btn btn-sm btn-ghost" onClick={clear}><Icon name="trash" />Apagar histórico</button>} />

      <div className="chat-layout">
        <section className="card chat-card" aria-label="Mensagens">
          <div className="chat-list" ref={listRef} aria-live="polite">
            {!c.historyLoaded && <Skeleton kind="line" />}
            {empty && (
              <div className="chat-empty">
                <Icon name="chat" />
                <p>Comece pelo que quiser: planejar a semana, destravar um projeto, escrever uma mensagem, pensar uma ideia.</p>
                <div className="chips">
                  {['Quais as principais notícias de hoje?', 'Tem algo no meu e-mail para responder?', 'Como estão meus projetos?', 'Me ajude a planejar amanhã'].map((s) => (
                    <button key={s} type="button" className="chip" onClick={() => void submitText(s)}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {c.messages.map((m) => (
              <div key={m.id} className={`chat-msg ${m.role}`}>
                <span className="chat-who">{m.role === 'user' ? 'VOCÊ' : 'JARVIS'}</span>
                <div className="chat-text">{m.content}</div>
                {m.sources && <Sources items={m.sources} />}
              </div>
            ))}
            {c.streaming && (
              <>
                <div className="chat-msg user"><span className="chat-who">VOCÊ</span><div className="chat-text">{c.pendingQuestion}</div></div>
                <div className="chat-msg assistant live"><span className="chat-who">JARVIS</span>
                  {c.searching && <span className="chat-searching"><Icon name="search" />Pesquisando na web: “{c.searching}”</span>}
                  <div className="chat-text">{c.draft || <span className="chat-thinking">{c.searching ? 'lendo os resultados' : 'pensando'}</span>}<i className="chat-caret" aria-hidden="true" /></div>
                  <Sources items={c.liveSources} />
                </div>
              </>
            )}
            {c.notice && <Notice icon="info">{c.notice}</Notice>}
            {c.error && <Notice kind="warn" icon="alert">{c.error.message} <span className="mono">[{c.error.code}]</span></Notice>}
            <LearnedNotes />
            <Suggestions />
          </div>

          <form className="chat-composer" onSubmit={onSubmit}>
            <label htmlFor="chat-input" className="sr-only">Mensagem para o Jarvis</label>
            <textarea id="chat-input" className="input" rows={2} maxLength={4000}
              placeholder={v.listening ? 'Escutando…' : 'Escreva ou fale com o Jarvis. Enter envia, Shift+Enter quebra a linha.'}
              value={v.listening ? v.interim : text} readOnly={v.listening}
              onChange={(e) => setText(e.target.value)} onKeyDown={onKey} />
            <div className="chat-actions">
              <button type="button" className={`btn btn-icon ${v.listening ? 'live' : ''}`} disabled={!v.supported.listen || c.streaming}
                title={micTitle} aria-label={micTitle} aria-pressed={v.listening}
                onClick={() => (v.listening ? voice.stop() : voice.listen())}><Icon name="mic" /></button>
              {c.streaming
                ? <button type="button" className="btn" onClick={() => chat.stop()}><Icon name="pause" />Parar</button>
                : <button type="submit" className="btn btn-primary" disabled={!text.trim()}><Icon name="send" />Enviar</button>}
            </div>
          </form>
          <div className="chat-foot">
            <label className="vb-toggle"><input type="checkbox" checked={speak} onChange={(e) => { setSpeak(e.target.checked); if (!e.target.checked) voice.hush(); }} /><span>responder falando</span></label>
            <span><Icon name="lock" />Suas mensagens vão para o servidor do Jarvis e para a Anthropic, que gera a resposta. O histórico fica guardado até você apagar.</span>
          </div>
        </section>

        <aside className="stack">
          <UsagePanel />
          <section className="card">
            <CardHead icon="brain" title="Memória" />
            <p className="row-sub">O Jarvis aprende sozinho o que você conta sobre você e avisa na hora, com opção de desfazer. Também lembra de conversas antigas quando o assunto volta.</p>
            {toReview > 0 && <Notice icon="brain">{toReview} {toReview === 1 ? 'coisa aprendida espera' : 'coisas aprendidas esperam'} a sua revisão.</Notice>}
            <div className="card-foot"><a className="btn btn-sm" href="#/configuracoes/memoria"><Icon name="edit" />{toReview > 0 ? 'Revisar agora' : 'Ver e editar memória'}</a></div>
          </section>
        </aside>
      </div>
    </div>
  );
}
